import {useLayoutEffect} from '@github-ui/use-layout-effect'
import {type RefObject, useState} from 'react'
import {createPortal} from 'react-dom'

import {validateSuggestedChangeCanBeApplied} from '../../helpers/suggested-changes'
import {extractSuggestionFromDOM} from '../../helpers/extract-suggestion-content'
import type {
  ApplySuggestedChangesValidationData,
  CommentWithoutFragment,
  SuggestedChange,
  SuggestedChangesConfiguration,
} from '../../conversation-types'
import {CodeSuggestionActions} from './CodeSuggestionActions'
import {CodeSuggestionUnavailable} from './CodeSuggestionActions/CodeSuggestionUnavailable'
import {useHasCommitRange, usePullRequest} from '@github-ui/pull-requests/pull-request-store-selectors'

function useSuggestedChangesRefs(
  commentBodyRef: React.RefObject<HTMLDivElement | null>,
  comment: CommentWithoutFragment,
  filePath: string,
  threadId: number | null | undefined,
  applySuggestedChangesValidationData: ApplySuggestedChangesValidationData,
) {
  const [suggestedChangeRefs, setSuggestedChangeRefs] = useState<SuggestedChangeWithRef[] | undefined>()

  // This useLayoutEffect will search the comment body for div.js-apply-changes elements.
  // The apply changes nodes will be stored in an Array and used as refs with React's createPortal.
  // The rest of the suggested change info will be stored in state to be used for batching or applying suggestions.
  // We choose to use useLayoutEffect here because we want to run this code before the comment body is rendered so we don't
  // flash content.
  useLayoutEffect(() => {
    if (!commentBodyRef.current) return
    if (comment.subjectType?.toUpperCase() !== 'LINE') return

    const renderedApplyChangeElements = [...commentBodyRef.current.getElementsByClassName('js-apply-changes')]
    if (renderedApplyChangeElements.length > 0) {
      const suggestedChanges: SuggestedChangeWithRef[] = []

      for (let i = 0; i < renderedApplyChangeElements.length; i++) {
        const ref = renderedApplyChangeElements[i]
        if (!ref) continue
        const changesBlobEl = ref.closest('div.js-suggested-changes-blob')
        if (!changesBlobEl) continue
        if (!applySuggestedChangesValidationData.lineRange) continue

        const suggestionLines = extractSuggestionFromDOM(changesBlobEl)

        suggestedChanges.push({
          authorLogin: comment.author?.login ?? 'ghost',
          commentId: comment.id,
          commentDatabaseId: comment.databaseId,
          path: filePath,
          ref,
          suggestion: suggestionLines,
          lineRange: applySuggestedChangesValidationData.lineRange,
          threadId,
          suggestionIndex: i,
        })
      }
      setSuggestedChangeRefs(suggestedChanges)
    }
  }, [
    applySuggestedChangesValidationData.lineRange,
    comment.author?.login,
    comment.bodyHTML,
    comment.databaseId,
    comment.id,
    comment.subjectType,
    commentBodyRef,
    filePath,
    threadId,
  ])

  return suggestedChangeRefs
}

type SuggestedChangeWithRef = SuggestedChange & {ref: Element}

export interface SuggestedChangeViewProps {
  applySuggestedChangesValidationData: ApplySuggestedChangesValidationData
  comment: CommentWithoutFragment
  commentBodyRef: RefObject<HTMLDivElement | null>
  filePath: string
  isOutdated?: boolean
  isThreadResolved: boolean
  suggestedChangesConfig?: SuggestedChangesConfiguration
  databaseId: number | null | undefined
  viewerCanApplySuggestion?: boolean
}

export function SuggestedChangeView({
  applySuggestedChangesValidationData,
  comment,
  commentBodyRef,
  filePath,
  isOutdated,
  isThreadResolved,
  suggestedChangesConfig,
  databaseId,
  viewerCanApplySuggestion,
}: SuggestedChangeViewProps): React.ReactElement {
  const {state: pullRequestState} = usePullRequest()
  const hasCommitRange = useHasCommitRange()

  const suggestedChangeRefs = useSuggestedChangesRefs(
    commentBodyRef,
    comment,
    filePath,
    databaseId,
    applySuggestedChangesValidationData,
  )

  return (
    <>
      {/* Insert suggested change button group inside of each div.js-apply-changes element. */}
      {suggestedChangeRefs?.length
        ? suggestedChangeRefs.map(suggestedChange => {
            const {isValid, reason} = validateSuggestedChangeCanBeApplied({
              suggestedChange,
              hasCommitRange,
              isOutdated,
              isPending: comment.state.toUpperCase() === 'PENDING',
              isResolved: isThreadResolved,
              pullRequestIsClosed:
                pullRequestState === undefined || pullRequestState === 'CLOSED' || pullRequestState === 'MERGED',
              pullRequestIsInMergeQueue: pullRequestState === 'QUEUED',
              userCanApplySuggestion: !!viewerCanApplySuggestion,
              applySuggestedChangesValidationData,
              suggestedChangesConfig,
            })
            if (!isValid && reason) {
              return createPortal(<CodeSuggestionUnavailable reason={reason} />, suggestedChange.ref)
            }
            return createPortal(<CodeSuggestionActions suggestedChange={suggestedChange} />, suggestedChange.ref)
          })
        : null}
    </>
  )
}

try{ SuggestedChangeView.displayName ||= 'SuggestedChangeView' } catch {}